import React, { useState, useEffect, useContext, useMemo } from 'react';
import { RouterConfig } from './routerConfig';
import {
    Router,
    RouterEnhancer,
    RouterLinkProps,
    RouterLinkComponent,
} from './routerInterface';
import { RouterRootViewProps } from './root';
import { RouterRouteProps } from './route/routerRouteProps';
import { Link as ReactRouterLink } from 'react-router-dom';
import {
    RouterStrategy,
    createHashStrategy,
    createMemoryStrategy,
    MemoryRouterStrategy,
} from './strategies';
import { createRouterContext, RouterContextType } from './routerContext';
import { RouterBackButtonProps } from './back';

// Helper function to extract parameters from a path pattern
function extractParams(pattern: string, path: string): Record<string, string> | null {
    const patternParts = pattern.split('/');
    const pathParts = path.split('/');

    if (patternParts.length !== pathParts.length) {
        return null;
    }

    const params: Record<string, string> = {};

    for (let i = 0; i < patternParts.length; i++) {
        const patternPart = patternParts[i];
        const pathPart = pathParts[i];

        if (patternPart.startsWith(':')) {
            // This is a parameter
            const paramName = patternPart.slice(1);
            params[paramName] = pathPart;
        } else if (patternPart !== pathPart) {
            // Static parts don't match
            return null;
        }
    }

    return params;
}

// Get the appropriate strategy based on configuration
function getRouterStrategy(config: RouterConfig): RouterStrategy {
    if (config.state.kind === 'hash') {
        return createHashStrategy();
    }

    if (config.state.kind === 'memory') {
        return createMemoryStrategy(config.state.initialPath);
    }

    // Default to hash strategy for now
    return createHashStrategy();
}

export function createRouter(config: RouterConfig, enhancer?: RouterEnhancer): Router {
    if (enhancer) {
        return enhancer(createRouter)(config);
    }

    // Create a unique context for this router instance
    const RouterContext = createRouterContext();

    // Get the appropriate strategy based on configuration
    const strategy = getRouterStrategy(config);

    // Create hooks for this specific router instance
    const useNavigate = () => {
        const context = useContext(RouterContext);
        return (path: string | null, replace: boolean = false) => {
            strategy.navigate(path, replace);
        };
    };

    const useLocation = () => {
        const context = useContext(RouterContext);
        return { pathname: context.currentPath };
    };

    const useParams = <TKey extends string>(keys: TKey[]): Record<TKey, string> => {
        const context = useContext(RouterContext);
        const allParams = context.params || {};

        // Create a typed object with only the requested keys
        const result = {} as Record<TKey, string>;
        for (const key of keys) {
            result[key] = allParams[key] || '';
        }

        return result;
    };

    // Link component that uses the router's navigation
    // If React Router is available, we can wrap it; otherwise, use our custom implementation
    const Link: RouterLinkComponent = <C extends React.ElementType = 'a'>({
        to,
        replace = false,
        children,
        className,
        style,
        onClick,
        as,
        ...rest
    }: RouterLinkProps<C>) => {
        const Component = as ?? 'a';
        const context = useContext(RouterContext);

        // Common click handler for both implementations
        const handleNavClick = (e: React.MouseEvent) => {
            // Let the browser handle the event if:
            // 1. It's not a left click
            // 2. Meta keys are pressed (Ctrl, Alt, Shift, Meta)
            if (e.button !== 0 || e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) {
                return;
            }

            e.preventDefault();

            // Call the provided onClick handler if any
            if (onClick) {
                onClick(e);
            }

            // Navigate to the destination using the strategy directly
            strategy.navigate(to, replace);
        };

        // If React Router is available in the project, try to use its Link
        try {
            if (Component === 'a') {
                // Only use ReactRouterLink if we're rendering as an 'a' tag
                // Create a URL for React Router Link
                const url = strategy.createUrl(to);

                // Use React Router's Link component but intercept its behavior
                return (
                    <ReactRouterLink
                        to={url}
                        className={className}
                        style={style}
                        onClick={handleNavClick}
                        {...rest}
                    >
                        {children}
                    </ReactRouterLink>
                );
            }
        } catch (e) {
            // If ReactRouterLink is not available, fall back to our implementation
        }

        // For custom components or when React Router is not available
        const linkProps =
            Component === 'a'
                ? {
                      href: strategy.createUrl(to),
                      className,
                      style,
                      onClick: handleNavClick,
                      ...rest,
                  }
                : {
                      className,
                      style,
                      onClick: handleNavClick,
                      ...rest,
                  };

        return <Component {...linkProps}>{children}</Component>;
    };

    // BackButton component that navigates back using the router
    const BackButton: React.FC<RouterBackButtonProps> = ({
        as: ButtonComponent,
        ...buttonProps
    }) => {
        const navigate = useNavigate();

        const handleBackClick = () => {
            navigate(null); // Navigate back to root
        };

        return <ButtonComponent {...buttonProps} onClick={handleBackClick} />;
    };

    return {
        Component: {
            Root(props: RouterRootViewProps) {
                const [currentPath, setCurrentPath] = useState<string>(() =>
                    strategy.getCurrentPath()
                );
                const [outletContent, setOutletContent] =
                    useState<React.ReactElement | null>(null);

                // Listen for routing changes based on strategy type
                useEffect(() => {
                    let cleanup: (() => void) | undefined;

                    if (config.state.kind === 'hash') {
                        const handleHashChange = () => {
                            setCurrentPath(strategy.getCurrentPath());
                        };

                        window.addEventListener('hashchange', handleHashChange);
                        cleanup = () => {
                            window.removeEventListener('hashchange', handleHashChange);
                        };
                    } else if (config.state.kind === 'memory') {
                        // For memory strategy, subscribe to path changes
                        const memoryStrategy = strategy as MemoryRouterStrategy;
                        cleanup = memoryStrategy.subscribe((newPath) => {
                            setCurrentPath(newPath);
                        });
                    }

                    return cleanup;
                }, []);

                // Navigation function for this context
                const navigate = (path: string | null, replace: boolean = false) => {
                    strategy.navigate(path, replace);
                };

                // Provide the routing context
                return (
                    <RouterContext.Provider
                        value={{
                            currentPath,
                            navigate,
                            outletContent,
                            setOutletContent,
                        }}
                    >
                        {props.children}
                    </RouterContext.Provider>
                );
            },
            Route(props: RouterRouteProps) {
                const context = useContext(RouterContext);
                const { currentPath, setOutletContent } = context;

                // For layout routes (routes with children)
                if ('children' in props) {
                    // Find the matching child route
                    const childRoutes = Array.isArray(props.children)
                        ? props.children
                        : [props.children];

                    for (const childRoute of childRoutes) {
                        const childProps = childRoute.props;

                        // Check if this child route matches the current path
                        if (
                            'index' in childProps &&
                            childProps.index &&
                            currentPath === ''
                        ) {
                            // Set the outlet content and render the layout
                            setOutletContent?.(childProps.element);
                            return props.element;
                        }

                        if ('path' in childProps) {
                            const routePath = childProps.path;

                            // Check for exact match
                            if (currentPath === routePath) {
                                setOutletContent?.(childProps.element);
                                return props.element;
                            }

                            // Check for parameter match
                            const params = extractParams(routePath, currentPath);
                            if (params !== null) {
                                // Update the context with extracted parameters
                                (context as any).params = params;
                                setOutletContent?.(childProps.element);
                                return props.element;
                            }
                        }
                    }

                    // No child route matched, don't render the layout
                    setOutletContent?.(null);
                    return null;
                }

                // For index routes
                if ('index' in props && props.index) {
                    return currentPath === '' ? props.element : null;
                }

                // For path-based routes
                if ('path' in props) {
                    const routePath = props.path;

                    // Check for exact match first
                    if (currentPath === routePath) {
                        return props.element;
                    }

                    // Check for parameter match
                    const params = extractParams(routePath, currentPath);
                    if (params !== null) {
                        // Update the context with extracted parameters
                        // Note: This is a bit of a hack, but works for our use case
                        (context as any).params = params;
                        return props.element;
                    }
                }

                return null;
            },
            Link,
            BackButton,
            Outlet() {
                const context = useContext(RouterContext);
                return context.outletContent || null;
            },
        },
        useNavigate,
        useLocation,
        useParams,
    };
}
